/**
 * Servicio: Órdenes de Producción (AMX-PR-FR-01).
 *
 * ⚠️ No confundir con las Órdenes de Trabajo de `produccion.service.js`, que son
 * de POSVENTA — mantenimiento y reparación en campo. Esto es el ensamble en planta.
 */

import api from './api.js';

class OrdenProduccionService {
  limpiarParams(params = {}) {
    const out = {};
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') out[k] = v;
    }
    return out;
  }

  async listar(params = {}) {
    return await api.get('/produccion/ordenes-produccion', this.limpiarParams(params));
  }

  async obtener(id) {
    return await api.get(`/produccion/ordenes-produccion/${id}`);
  }

  async estadisticas() {
    return await api.get('/produccion/ordenes-produccion/estadisticas');
  }

  /** La orden viva de un tractor (404 si no tiene). */
  async abiertaPorSerie(numSerie) {
    return await api.get(`/produccion/ordenes-produccion/tractor/${encodeURIComponent(numSerie)}/abierta`);
  }

  async crear(data) {
    return await api.post('/produccion/ordenes-produccion', data);
  }

  async actualizar(id, data) {
    return await api.put(`/produccion/ordenes-produccion/${id}`, data);
  }

  // ── Avance del trabajo ────────────────────────────────────────────────────
  // Cada una deja su huella en la bitácora de la unidad. El servidor valida que
  // la transición sea legal; aquí sólo se pintan los botones que aplican.

  async iniciar(id, data = {}) {
    return await api.patch(`/produccion/ordenes-produccion/${id}/iniciar`, data);
  }

  async pausar(id, data) {
    return await api.patch(`/produccion/ordenes-produccion/${id}/pausar`, data);
  }

  async reanudar(id, data = {}) {
    return await api.patch(`/produccion/ordenes-produccion/${id}/reanudar`, data);
  }

  async terminar(id, data = {}) {
    return await api.patch(`/produccion/ordenes-produccion/${id}/terminar`, data);
  }

  async cancelar(id, data) {
    return await api.patch(`/produccion/ordenes-produccion/${id}/cancelar`, data);
  }

  async eliminar(id) {
    return await api.delete(`/produccion/ordenes-produccion/${id}`);
  }

  // ── Autorizaciones del jefe de producción ─────────────────────────────────
  // Lo que se mide aquí no es quién firmó, es CUÁNTO TARDÓ en firmar. Por eso
  // pedir y resolver son dos llamadas distintas y no un solo "marcar autorizado".

  async autorizaciones(ordenId) {
    return await api.get(`/produccion/ordenes-produccion/${ordenId}/autorizaciones`);
  }

  /** `pausar: true` detiene la orden con motivo «esperando autorización». */
  async solicitarAutorizacion(ordenId, data) {
    return await api.post(`/produccion/ordenes-produccion/${ordenId}/autorizaciones`, data);
  }

  /** `estado` es 'autorizada' o 'rechazada'; rechazar exige `motivo_rechazo`. */
  async resolverAutorizacion(autId, data) {
    return await api.patch(`/produccion/ordenes-produccion/autorizaciones/${autId}`, data);
  }

  /** Lo que el jefe trae encima ahora mismo, lo más viejo primero. */
  async autorizacionesPendientes() {
    return await api.get('/produccion/ordenes-produccion/autorizaciones/pendientes');
  }

  // ── Catálogos de la pantalla ──────────────────────────────────────────────
  // Copia de los enums del servidor. Si allá se agrega uno, se agrega aquí.
  get TIPOS() {
    return [
      { valor: 'ensamble', texto: 'Ensamble' },
      { valor: 'reparacion', texto: 'Reparación' },
      { valor: 'modificacion', texto: 'Modificación' },
    ];
  }

  get PRIORIDADES() {
    return [
      { valor: 'alta', texto: 'Alta' },
      { valor: 'media', texto: 'Media' },
      { valor: 'baja', texto: 'Baja' },
    ];
  }

  /** Espejo del enum `tipo` de orden_produccion_autorizaciones. */
  get TIPOS_AUTORIZACION() {
    return [
      { valor: 'inicio_trabajo', texto: 'Arrancar el trabajo' },
      { valor: 'refaccion', texto: 'Refacción o material' },
      { valor: 'trabajo_adicional', texto: 'Trabajo adicional' },
      { valor: 'horas_extra', texto: 'Horas extra' },
      { valor: 'cierre', texto: 'Cerrar la orden' },
      { valor: 'otro', texto: 'Otro' },
    ];
  }

  /** Los motivos de paro son el dato que explica los tiempos muertos. */
  get MOTIVOS_PAUSA() {
    return [
      { valor: 'falta_refaccion', texto: 'Falta de refacción' },
      { valor: 'falta_personal', texto: 'Falta de personal' },
      { valor: 'pnc', texto: 'Producto no conforme' },
      { valor: 'espera_autorizacion', texto: 'Esperando autorización' },
      { valor: 'otro', texto: 'Otro' },
    ];
  }

  /** Qué se puede hacer desde cada estado. Espejo del servidor. */
  accionesDe(estado) {
    return {
      abierta: ['iniciar', 'cancelar'],
      en_proceso: ['pausar', 'terminar', 'cancelar'],
      pausada: ['reanudar', 'cancelar'],
      terminada: [],
      cancelada: [],
    }[estado] || [];
  }
}

export default new OrdenProduccionService();
